<template>
  <div
    v-if="visible"
    class="utility-announcement"
    :class="[
      { 'utility-announcement--closing': closing, 'beta-update': beta },
      sidebar !== 'none' ? `utility-announcement--beside-${sidebar}` : null,
      beta && sidebar !== 'none' ? `beta-update--beside-${sidebar}` : null,
      beta && closing ? 'beta-update--closing' : null
    ]"
    :data-testid="testId"
    role="region"
    :aria-label="regionLabel"
    @transitionend.self="onCollapsed"
  >
    <div class="utility-announcement__clip">
      <div class="container utility-announcement__content">
        <p class="utility-announcement__text" :class="{ 'beta-update__text': beta }">
          <slot />
        </p>
        <button
          type="button"
          class="utility-announcement__close"
          :class="{ 'beta-update__close': beta }"
          :data-testid="dismissTestId"
          :aria-label="dismissLabel"
          @click="dismiss"
        >
          <q-icon name="close" size="18px" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';

withDefaults(
  defineProps<{
    visible: boolean;
    regionLabel: string;
    dismissLabel: string;
    testId: string;
    dismissTestId: string;
    sidebar?: 'none' | 'expanded' | 'collapsed';
    /** Retains the rollout banner's selectors for existing consumers. */
    beta?: boolean;
  }>(),
  { sidebar: 'none', beta: false }
);

const emit = defineEmits<{ dismiss: []; collapsed: [] }>();
const closing = ref(false);

function dismiss(): void {
  emit('dismiss');
  if (
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  ) {
    emit('collapsed');
    return;
  }
  closing.value = true;
}

function onCollapsed(event: Event): void {
  if (
    !closing.value ||
    (event as Event & { propertyName?: string }).propertyName !== 'grid-template-rows'
  )
    return;
  closing.value = false;
  emit('collapsed');
}
</script>

<style scoped lang="scss">
.utility-announcement {
  background: color-mix(in srgb, var(--g-accent) 18%, var(--g-bg-surface));
  border-bottom: var(--g-hairline) solid var(--g-accent-stroke);
  color: var(--g-text-primary);
  display: grid;
  grid-template-rows: 1fr;
  opacity: 1;
  transition:
    grid-template-rows 220ms ease,
    opacity 160ms ease,
    border-color 220ms ease;
  animation: utility-announcement-enter 320ms ease-out;
}

.utility-announcement--beside-expanded {
  margin-left: var(--g-drawer-width);
}
.utility-announcement--beside-collapsed {
  margin-left: var(--g-drawer-width-collapsed);
}
.utility-announcement--closing {
  border-bottom-color: transparent;
  grid-template-rows: 0fr;
  opacity: 0;
}

@keyframes utility-announcement-enter {
  from {
    grid-template-rows: 0fr;
    opacity: 0;
  }
  to {
    grid-template-rows: 1fr;
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .utility-announcement {
    animation: none;
    transition: none;
  }
}

.utility-announcement__clip {
  min-height: 0;
  overflow: hidden;
}
.utility-announcement__content {
  align-items: center;
  display: grid;
  gap: var(--space-2);
  grid-template-columns: var(--g-button-min-height) minmax(0, 1fr) var(--g-button-min-height);
  padding-inline: var(--space-2);
}
.utility-announcement__text {
  font-size: 0.75rem;
  font-weight: 500;
  grid-column: 2;
  line-height: 1.4;
  margin: 0;
  padding-block: var(--space-2);
  text-align: center;
  text-wrap: pretty;
}
.utility-announcement__close {
  align-items: center;
  appearance: none;
  background: transparent;
  border: 0;
  border-radius: var(--g-radius-full);
  color: var(--g-text-secondary);
  cursor: pointer;
  display: inline-flex;
  grid-column: 3;
  height: var(--g-button-min-height);
  justify-content: center;
  padding: 0;
  width: var(--g-button-min-height);
}
.utility-announcement__close:hover {
  color: var(--g-text-primary);
}
.utility-announcement__close:focus-visible {
  outline: 2px solid var(--g-accent-text);
  outline-offset: -4px;
}

@media (max-width: 600px) {
  .utility-announcement__content {
    align-items: start;
    gap: var(--space-1);
    grid-template-columns: minmax(0, 1fr) var(--g-button-min-height);
    padding-inline: var(--space-4) 0;
  }
  .utility-announcement__text {
    grid-column: 1;
    padding-block: var(--space-3);
    text-align: left;
  }
  .utility-announcement__close {
    grid-column: 2;
  }
}
</style>
